/* Persistent cart helpers — Feb 2026.
 *
 * Cart now lives on the server (collection: store_carts, keyed by franchise_id).
 * This module provides:
 *   • In-memory snapshot + localStorage cache for instant UI reads.
 *   • Async write-through helpers (add / setQty / remove / clear / replace).
 *   • A pub/sub bus ("storeCartChanged" event) so header + pages stay in sync
 *     without re-fetching.
 *   • A boot-time hydrate so refresh / re-login / multi-device all show the
 *     same persisted cart.
 *
 * The cart shape used by UI is still `{ [product_id]: { product, qty } }` so
 * existing components (StoreCatalogue, StoreCart) didn't need a rewrite.
 */
import { storeApi } from './storeApi';

const CART_CACHE_KEY = 'store_cart';

function _readCache() {
  try {
    const raw = localStorage.getItem(CART_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function _writeCache(cart) {
  try { localStorage.setItem(CART_CACHE_KEY, JSON.stringify(cart)); }
  catch { /* quota — ignore, cart is also on the server */ }
}

function _emit(cart, extra = {}) {
  _writeCache(cart);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('storeCartChanged', { detail: { cart, ...extra } }));
  }
}

/** Convert server cart payload → UI-shape map keyed by product_id. */
function _serverToMap(serverPayload) {
  const map = {};
  for (const it of (serverPayload?.items || [])) {
    if (it.product && it.product.id) {
      map[it.product.id] = { product: it.product, qty: Number(it.quantity || 0) };
    }
  }
  return map;
}

/** Optimistic local mutation that mirrors what the server will do. */
function _applyDelta(prev, productId, product, deltaOrTarget, absolute) {
  const next = { ...prev };
  const cur = Number(next[productId]?.qty || 0);
  const final = absolute ? Number(deltaOrTarget) : cur + Number(deltaOrTarget);
  if (final <= 0) {
    delete next[productId];
  } else {
    next[productId] = { product: product || next[productId]?.product, qty: final };
  }
  return next;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Synchronous read of the cached cart — for instant first paint. */
export function getCart() { return _readCache(); }

/** Sync count helper — backwards compatible (existing pages call this). */
export function cartCount(cart) {
  return Object.values(cart || {}).reduce((s, l) => s + Number(l.qty || 0), 0);
}

/** Cache write — kept for legacy code paths; prefer the typed helpers below. */
export function saveCart(cart) { _emit(cart); }

/** Boot-time hydrate. Called from StoreLayout / StoreCatalogue on mount.
 *  Returns the live cart object. If the server has items, also returns a
 *  `restored` flag (true when the cache was empty but the server had items
 *  — used to show the "Your previous cart has been restored" toast).
 */
export async function hydrateCartFromServer() {
  const cached = _readCache();
  const wasCachedEmpty = Object.keys(cached).length === 0;
  try {
    const r = await storeApi.get('/store/cart');
    const map = _serverToMap(r.data);
    _emit(map, { source: 'hydrate' });
    return {
      cart: map,
      restored: wasCachedEmpty && Object.keys(map).length > 0,
      cleared_reason: r.data?.cleared_reason || null,
    };
  } catch (_) {
    // Network blip — UI keeps using the local cache; next mutation re-syncs.
    return { cart: cached, restored: false, cleared_reason: null };
  }
}

/** Add or subtract `delta` of one product. Returns the new cart map. */
export async function cartAdd(product, delta) {
  if (!product?.id || !delta) return _readCache();
  const optimistic = _applyDelta(_readCache(), product.id, product, delta, false);
  _emit(optimistic, { source: 'optimistic-add' });
  try {
    const r = await storeApi.post('/store/cart/items',
      { product_id: product.id, quantity_delta: Number(delta) });
    const server = _serverToMap(r.data);
    _emit(server, { source: 'add' });
    return server;
  } catch (e) {
    // Roll back to last known good — re-fetch.
    const fresh = await hydrateCartFromServer().catch(() => null);
    throw e?.response?.data?.detail
      ? new Error(e.response.data.detail)
      : (e || new Error('Failed to update cart'));
  }
}

/** Set absolute qty for one product; 0 removes the line. */
export async function cartSetQty(product, qty) {
  if (!product?.id) return _readCache();
  const optimistic = _applyDelta(_readCache(), product.id, product, qty, true);
  _emit(optimistic, { source: 'optimistic-set' });
  try {
    const r = await storeApi.patch(`/store/cart/items/${product.id}`,
      { quantity: Math.max(0, Math.floor(Number(qty) || 0)) });
    const server = _serverToMap(r.data);
    _emit(server, { source: 'set' });
    return server;
  } catch (e) {
    await hydrateCartFromServer().catch(() => null);
    throw e?.response?.data?.detail ? new Error(e.response.data.detail) : e;
  }
}

/** Remove one product entirely. */
export async function cartRemove(productId) {
  const next = { ..._readCache() };
  delete next[productId];
  _emit(next, { source: 'optimistic-remove' });
  try {
    const r = await storeApi.delete(`/store/cart/items/${productId}`);
    const server = _serverToMap(r.data);
    _emit(server, { source: 'remove' });
    return server;
  } catch (e) {
    await hydrateCartFromServer().catch(() => null);
    throw e?.response?.data?.detail ? new Error(e.response.data.detail) : e;
  }
}

/** Hard-clear — called after order success, or by an explicit "clear cart" button. */
export async function cartClear() {
  _emit({}, { source: 'optimistic-clear' });
  try {
    await storeApi.delete('/store/cart');
  } catch (_) { /* still locally cleared */ }
  return {};
}

/** Push a complete cart map to the server (used as last-resort sync after
 *  a long offline window). De-dupe + qty floor handled server-side. */
export async function cartReplace(cartMap) {
  const items = Object.values(cartMap || {}).map((l) => ({
    product_id: l.product?.id,
    quantity: Math.max(0, Math.floor(Number(l.qty) || 0)),
  })).filter((i) => i.product_id);
  try {
    const r = await storeApi.put('/store/cart', { items });
    const server = _serverToMap(r.data);
    _emit(server, { source: 'replace' });
    return server;
  } catch (_) { return cartMap; }
}
